import { useId } from "react";
import { cn } from "@/lib/utils";

const EMBLEM_MASK = "/logos/paranine-emblem-mask.png";
const WORDMARK_MASK = "/logos/paranine-wordmark-mask.png";
const NAME_MASK = "/logos/paranine-name-mask.png";

/** The supplied paranine emblem, rendered in the current theme colour. */
export function LogoMark({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      role="presentation"
      className={cn("block shrink-0 bg-current", className)}
      style={{
        WebkitMaskImage: `url(${EMBLEM_MASK})`,
        maskImage: `url(${EMBLEM_MASK})`,
        WebkitMaskPosition: "center",
        maskPosition: "center",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskSize: "contain",
        maskSize: "contain",
      }}
    />
  );
}

/** The supplied full paranine lockup, rendered in the current theme colour. */
export function LogoWordmark({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      role="presentation"
      className={cn("block shrink-0 bg-current", className)}
      style={{
        WebkitMaskImage: `url(${WORDMARK_MASK})`,
        maskImage: `url(${WORDMARK_MASK})`,
        WebkitMaskPosition: "center",
        maskPosition: "center",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskSize: "contain",
        maskSize: "contain",
      }}
    />
  );
}

/** The name portion of the supplied lockup, for layouts that place the emblem separately. */
export function LogoName({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      role="presentation"
      className={cn("block shrink-0 bg-current", className)}
      style={{
        WebkitMaskImage: `url(${NAME_MASK})`,
        maskImage: `url(${NAME_MASK})`,
        WebkitMaskPosition: "center",
        maskPosition: "center",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskSize: "contain",
        maskSize: "contain",
      }}
    />
  );
}

/** The paranine emblem drawn into a parent SVG for product diagrams. */
export function SvgLogoMark({
  cx,
  cy,
  gap = 20,
  color = "var(--foreground)",
}: {
  cx: number;
  cy: number;
  gap?: number;
  r?: number;
  color?: string;
  diagColor?: string;
}) {
  const maskId = `paranine-emblem-${useId().replaceAll(":", "")}`;
  const height = gap * 2.8;
  const width = height * (876 / 1064);

  return (
    <g>
      <defs>
        <mask id={maskId} maskUnits="userSpaceOnUse" maskContentUnits="userSpaceOnUse">
          <image
            href={EMBLEM_MASK}
            x={cx - width / 2}
            y={cy - height / 2}
            width={width}
            height={height}
          />
        </mask>
      </defs>
      <rect
        x={cx - width / 2}
        y={cy - height / 2}
        width={width}
        height={height}
        fill={color}
        mask={`url(#${maskId})`}
      />
    </g>
  );
}

/** Full lockup. */
export function Logo({
  className,
  markClassName,
  compact = false,
}: {
  className?: string;
  markClassName?: string;
  compact?: boolean;
}) {
  return (
    <span className={cn("inline-flex items-center text-foreground select-none", className)}>
      {compact ? (
        <LogoMark className={cn("h-7 w-6", markClassName)} />
      ) : (
        <LogoWordmark className={cn("h-7 w-[7.75rem]", markClassName)} />
      )}
      <span className="sr-only">paranine</span>
    </span>
  );
}
